import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Grid, Card, CardMedia, Typography, Button, Box } from '@mui/material';
import { PlayArrow } from '@mui/icons-material'; // Import Play Icon
import ComingSoonDialog from './ComingSoonDialog'; // Import the new dialog
import AnalyticsService from "../services/AnalyticsService"; // Import the new service

const stories = [
  { id: 'hare-tortoise', title: 'The Tortoise and the Hare' },
  { id: 'godfather-opening-scene', title: 'The Godfather Opening Scene' },
  { id: '3-idiots-education-system', title: 'Sahastrabudhhe v Rancho' },
  { id: 'karna-kunti-part-1', title: 'Karna-Kunti : The Truth Part 1' },
  { id: 'karna-kunti-part-2', title: 'Karna-Kunti : The Truth Part 2' },
];

const Home = () => {
  const [commitId, setCommitId] = useState('');
  const [hoveredStory, setHoveredStory] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [showSubscription] = useState(false);

  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || navigator.maxTouchPoints > 0;
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    document.title = "Stories Home";
    AnalyticsService.trackPageView("/", "Home Page");
  }, []);

  const handleOpenDialog = () => {
    trackStoryClick("create", "create");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    fetch('/commit-id.txt')
      .then((res) => res.text())
      .then((text) => setCommitId(text.trim()))
      .catch(() => setCommitId('Unknown'));
  }, []);

  const trackStoryClick = (storyId, storyTitle) => {
    AnalyticsService.trackEvent('User', `Clicked on Story: ${storyTitle}`, storyId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box', // Include padding in the height calculation
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #0f2027, #203a43, #2c5364)', // Dark gradient BG
        color: '#fff',
        p: 3,
      }}
    >

      <ComingSoonDialog open={dialogOpen} onClose={handleCloseDialog} />

      {/* Page Title */}
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', textShadow: '2px 2px 5px rgba(0,0,0,0.5)' }}>
        Explore Stories
      </Typography>

      {/* Story Grid */}
      <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: '100%' }}>
        {/* Existing Story Cards */}
        {stories.map((story) => (
          <Grid item xs={6} sm="auto" key={story.id}>
            <Link to={`/play/${story.id}`} style={{ textDecoration: 'none' }} onClick={() => trackStoryClick(story.id, story.title)}>
              <Card
                sx={{
                  width: isIOS ? { xs: 150, sm: 170 } : { xs: 160, sm: 180 }, // Smaller for iOS
                  height: isIOS ? { xs: 210, sm: 230 } : { xs: 220, sm: 240 }, // Reduce height for iOS
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3, // Rounded corners
                  boxShadow: '0px 4px 10px rgba(255,255,255,0.2)', // Soft glow effect
                  transition: isMobile ? 'none' : 'transform 0.3s ease-in-out', // Disable scaling on mobile
                  cursor: 'pointer',
                  '&:hover': isMobile ? {} : { transform: 'scale(1.05)' }, // No hover scaling for mobile
                }}
                onMouseEnter={() => setHoveredStory(story.id)}
                onMouseLeave={() => setHoveredStory(null)}
              >
                {/* Cover Image */}
                <CardMedia
                  component="img"
                  height="100"
                  image={`/assets/covers/${story.id}.jpeg`}
                  alt={story.title}
                  sx={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: '3px 3px 0 0' }}
                />

                {/* Play Icon (Appears on Hover) */}
                {hoveredStory === story.id && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)', // Dark overlay
                      borderRadius: 3,
                      transition: 'opacity 0.3s ease-in-out',
                      opacity: 1,
                    }}
                  >
                    <PlayArrow sx={{ fontSize: 60, color: '#4caf50' }} />
                  </Box>
                )}

                {/* Story Title */}
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    padding: '8px 0',
                    transition: '0.2s ease-in-out',
                    '&:hover': { backgroundColor: '#1565c0' },
                  }}
                >
                  {story.title}
                </Button>
              </Card>
            </Link>
          </Grid>
        ))}

        {/* Create Your Own Story Card */}
        <Grid item xs={6} sm="auto">
          {/* <Link to="/create" style={{ textDecoration: 'none' }}> */}
            <Card
              sx={{
                width: isIOS ? { xs: 150, sm: 170 } : { xs: 160, sm: 180 }, // Smaller for iOS
                height: isIOS ? { xs: 210, sm: 230 } : { xs: 220, sm: 240 }, // Reduce height for iOS
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                boxShadow: '0px 4px 10px rgba(255,255,255,0.2)',
                transition: isMobile ? 'none' : 'transform 0.3s ease-in-out', // Disable scaling on mobile
                cursor: 'pointer',
                '&:hover': isMobile ? {} : { transform: 'scale(1.05)' }, // No hover scaling for mobile
              }}
              onClick={handleOpenDialog} // Open dialog on click
              onMouseEnter={() => setHoveredStory('create')}
              onMouseLeave={() => setHoveredStory(null)}
            >
              {/* Cover Image */}
              <CardMedia
                component="img"
                height="100"
                image={`/assets/covers/create-story.jpeg`} // Placeholder cover image
                alt="Create Your Own"
                sx={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: '3px 3px 0 0' }}
              />

              {/* Play Icon (Appears on Hover) */}
              {hoveredStory === 'create' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: 3,
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: 1,
                  }}
                >
                  <PlayArrow sx={{ fontSize: 60, color: '#87CEEB' }} />
                </Box>
              )}

              {/* Button Title */}
              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  padding: '8px 0',
                  transition: '0.2s ease-in-out',
                  '&:hover': { backgroundColor: '#388e3c' },
                }}
              >
                Create Your Own Story!
              </Button>
            </Card>
          {/* </Link> */}
        </Grid>
      </Grid>

      {showSubscription &&
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '30vh', // Keeps spacing on mobile
            mt: 'auto',
            position: { xs: 'static', md: 'absolute' }, // Static on mobile, absolute on desktop
            bottom: { md: 0 }, // Only applies on desktop
            left: { md: '50%' },
            transform: { md: 'translateX(-50%)' },
          }}
        >

          <Box
            sx={{
              width: '100%',
              maxWidth: 400, // Keep it responsive
              p: 2,
              borderRadius: 2,
              textAlign: 'center',
              bgcolor: 'rgba(13, 13, 13, 0.5)', // Light background
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
              Subscribe for Updates
            </Typography>


            <Box
              component="iframe"
              src="https://buttondown.com/coffee_cat_studios?as_embed=true&theme=dark"
              scrolling="no"
              sx={{
                width: '100%',
                height: 120, // Reduced height for mobile
                border: 'none',
              }}
            />
          </Box>
        </Box>}

      {/* Build ID */}
      {commitId && (
        <Typography
          variant="caption"
          sx={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          {commitId}
        </Typography>
      )}
    </Box>
  );
};

export default Home;
